import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import {
  getNotifications,
  markAsRead as markAsReadRequest,
  markAllAsRead as markAllAsReadRequest,
} from 'services/notifications/realtimeNotifications';

export interface NotificationItem {
  id: number;
  event: string;
  message: string;
  data: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

interface NotificationState {
  notifications: NotificationItem[];
  unreadCount: number;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
};

const slice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setNotifications(state, action) {
      const payload = action.payload as {
        results?: NotificationItem[];
        unread_count?: number;
      };
      state.notifications = payload.results ?? [];
      state.unreadCount = payload.unread_count ?? 0;
    },
    addNotification(state, action: PayloadAction<unknown>) {
      const item = action.payload as NotificationItem;
      if (
        item?.id != null &&
        state.notifications.some((n) => n.id === item.id)
      ) {
        return;
      }
      if (item?.id != null) {
        state.notifications.unshift(item);
        if (!item.is_read) {
          state.unreadCount += 1;
        }
      }
    },
    markOneRead(state, action: PayloadAction<number>) {
      const notif = state.notifications.find((n) => n.id === action.payload);
      if (notif && !notif.is_read) {
        notif.is_read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllRead(state) {
      state.notifications.forEach((n) => {
        n.is_read = true;
      });
      state.unreadCount = 0;
    },
    resetNotifications() {
      return initialState;
    },
  },
});

export default slice.reducer;

export const { addNotification, resetNotifications } = slice.actions;

export const fetchNotifications = () => {
  getNotifications()
    .then((res) => dispatch(slice.actions.setNotifications(res)))
    .catch((err) => {
      console.warn('[Notifications] fetch failed — realtime API may be unavailable', err);
    });
};

export const markNotificationRead = (id: number) => {
  markAsReadRequest(id)
    .then(() => dispatch(slice.actions.markOneRead(id)))
    .catch(console.error);
};

export const markAllNotificationsRead = () => {
  markAllAsReadRequest()
    .then(() => dispatch(slice.actions.markAllRead()))
    .catch(console.error);
};
